import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Loader2 } from 'lucide-react';
import { Material, MaterialTariffHistory } from '../types/material.types';
import { useMaterialStore } from '../store/material-store';
import { useToast } from '@/lib/hooks/use-toast';

interface TariffHistoryDialogProps {
  material: Material | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TariffHistoryDialog({ material, open, onOpenChange }: TariffHistoryDialogProps) {
  const { toast } = useToast();
  const { addTariff, getTariffHistory, fetchMaterials } = useMaterialStore();
  const [history, setHistory] = useState<MaterialTariffHistory[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRate, setNewRate] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (material && open) {
      loadHistory();
    }
  }, [material, open]);

  const loadHistory = async () => {
    if (!material) return;
    setIsLoadingHistory(true);
    try {
      const data = await getTariffHistory(material.id);
      setHistory(data);
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo cargar el historial',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleAddTariff = async () => {
    if (!material || !newRate) return;
    setIsSaving(true);
    try {
      await addTariff(material.id, {
        rate: parseFloat(newRate),
        notes: newNotes || null,
      });
      toast({ title: 'Tarifa agregada', variant: 'success' });
      setShowAddForm(false);
      setNewRate('');
      setNewNotes('');
      await loadHistory();
      await fetchMaterials();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Historial de Gravamen Arancelario</DialogTitle>
          <DialogDescription>
            {material ? `${material.code} - ${material.name}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Add new tariff */}
          {!showAddForm ? (
            <Button onClick={() => setShowAddForm(true)} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Tarifa
            </Button>
          ) : (
            <div className="border rounded-lg p-4 space-y-3 bg-muted/50">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Nuevo Gravamen %</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newRate}
                    onChange={(e) => setNewRate(e.target.value)}
                    placeholder="5.0"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Notas</Label>
                  <Input
                    value={newNotes}
                    onChange={(e) => setNewNotes(e.target.value)}
                    placeholder="Motivo del cambio"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddTariff} disabled={isSaving || !newRate}>
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Guardar
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowAddForm(false)}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {/* History table */}
          {isLoadingHistory ? (
            <p className="text-center text-muted-foreground py-4">Cargando...</p>
          ) : history.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">Sin historial de tarifas</p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Gravamen %</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Desde</TableHead>
                    <TableHead>Hasta</TableHead>
                    <TableHead>Notas</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="font-mono font-semibold">{entry.rate}%</TableCell>
                      <TableCell>
                        <Badge variant={entry.isActive ? 'success' : 'default'}>
                          {entry.isActive ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(entry.validFrom).toLocaleDateString('es-ES')}
                      </TableCell>
                      <TableCell className="text-sm">
                        {entry.validTo ? new Date(entry.validTo).toLocaleDateString('es-ES') : '-'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {entry.notes || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TariffHistoryDialog;
